'use client'

import Link from 'next/link'
import { useButtonTracking } from '@/lib/useButtonTracking'

interface FooterProps {
  isMobile: boolean
}

const footerLinks = {
  resources: [
    { label: 'Community', href: '/#community' },
    { label: 'Courses', href: '/#courses' },
    { label: 'Free Courses', href: '/blog' },
    { label: 'Tools', href: '/#tools' },
    { label: 'Skills', href: '/skills' },
    { label: 'AI Builder Pack', href: '/#ai-builder-pack' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'About', href: '/about' },
  ],
  links: [
    { label: 'X (Twitter)', href: 'https://x.com/jasonzhou1993' },  
    { label: 'YouTube', href: 'https://www.youtube.com/@AIJasonZ' },
  ],
}

export default function Footer({ isMobile }: FooterProps) {
  const { trackButtonClick } = useButtonTracking()

  const handleLinkClick = (label: string, section: string) => {
    trackButtonClick(`footer_${label.toLowerCase()}`, {
      section: 'footer',
      subsection: section,
      action: 'click',
      button_name: label.toLowerCase(),
    })
  }

  return (
    <footer className="border-t border-white/10 bg-background">
      <div className={`max-w-5xl mx-auto px-4 sm:px-6 md:px-8 py-12 ${isMobile ? 'space-y-8' : ''}`}>
        <div className="max-w-3xl mx-auto">
          <div className={`${isMobile ? 'flex flex-col space-y-8' : 'flex flex-row justify-between'}`}>
            {/* Left side - Logo and description */}
            <div className={`${isMobile ? 'text-center' : 'md:max-w-xs'} mb-12 md:mb-0`}>
              <Link 
                href="/" 
                className={`text-xl font-bold ${isMobile ? 'block mb-4' : ''}`}
                onClick={() => handleLinkClick('logo', 'main')}
              >
                AI Builder Club
              </Link>
              <p className={`text-gray-400 mt-2 ${isMobile ? 'text-sm' : ''}`}>
                Join our community of AI builders and learn how to build AI-powered applications that solve real problems.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Built by <a href="https://x.com/jasonzhou1993" target="_blank" rel="noopener noreferrer" className="hover:underline">AI Jason</a>
              </p>
            </div>

            {/* Right side - Resources and Links columns */}
            <div className={`flex ${isMobile ? 'justify-around' : 'gap-16 md:gap-24'}`}>
              <div>
                <h3 className={`font-semibold mb-4 text-white ${isMobile ? 'text-sm' : ''}`}>Resources</h3>
                <ul className="space-y-2">
                  {footerLinks.resources.map((link) => (
                    <li key={link.label}>
                      <Link
                        href={link.href}
                        className={`text-gray-400 hover:text-white transition-colors ${isMobile ? 'text-sm' : ''}`}
                        onClick={() => handleLinkClick(link.label, 'resources')}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className={`font-semibold mb-4 text-white ${isMobile ? 'text-sm' : ''}`}>Links</h3>
                <ul className="space-y-2">
                  {footerLinks.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        href={link.href}
                        className={`text-gray-400 hover:text-white transition-colors ${isMobile ? 'text-sm' : ''}`}
                        onClick={() => handleLinkClick(link.label, 'links')}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className={`mt-12 pt-8 border-t border-white/10 ${isMobile ? 'text-center' : ''}`}>
            <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-400`}>
              © {new Date().getFullYear()} AI Builder Club. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
} 